// src/pages/profile/Profile.tsx - UPDATED
import React, { useState } from "react";
import { getSessionUser, updateSessionUser } from "../../utils/auth/sessionUser";
import { useQueryClient } from "@tanstack/react-query";
import { EditOutlined, SaveOutlined } from "@ant-design/icons";
import Layout from "../../components/Layout";
import { PageContainer, SectionCard, InlineLoading } from "../../components/shared";
import { useNotifications } from "../../context/NotificationContext";
import { updateProfile } from "../../api/admin/userApi";
import { updateOwnProfessionalInfo } from "../../api/admin/teamApi";
import { User } from "../../types/admin/UserTypes";
import { TeamMember } from "../../types/admin/TeamTypes";
import { isPractitionerColorConflictError, PRACTITIONER_COLOR_PALETTE_QUERY_KEY } from "../../types/admin/PractitionerColorTypes";
import SelfProfileBasicInfoSections from "./sections/SelfProfileBasicInfoSections";
import UserProfileProfessional from "./sections/UserProfileProfessional";
import UserProfileSchedule from "./sections/UserProfileSchedule";
import UserProfileEducation from "./sections/UserProfileEducation";
import UserProfileEmergency from "./sections/UserProfileEmergency";
import UserProfileBilling from "./sections/UserProfileBilling";
import { isStaffSession } from "../../utils/userPrefs/validatePermissions";
import { toSelfProfilePayload } from "../../utils/profile/selfProfileName";
import { Patient } from "../../types/healthcare/PatientTypes";

const ProfilePage: React.FC = () => {
	const { showSuccess, showError } = useNotifications();
	const queryClient = useQueryClient();
	const user = getSessionUser();
	// Seed synchronously from the cookie so child sections never receive null on first render.
	const [userData, setUserData] = useState<User | TeamMember | Patient | null>(
		() => (user.user_type === "clinical" ? (user as TeamMember) : (user as Patient))
	);
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);

	const isStaff = isStaffSession();

	const handleEditToggle = () => {
		setIsEditing(!isEditing);
	};

	const handleSave = async () => {

		try {
			await updateProfile(toSelfProfilePayload(userData));
		} catch (err) {
			console.error("Save error (profile):", err);
			showError("Failed to update profile");
			return;
		}

		if (isStaff) {
			try {
				await updateOwnProfessionalInfo((userData as TeamMember).team_member_info);
			} catch (err) {
				console.error("Save error (team member info):", err);
				if (isPractitionerColorConflictError(err)) {
					showError(
						"Your profile details saved, but that colour was just taken by someone else.",
						"Choose another colour and save again."
					);
					queryClient.invalidateQueries({ queryKey: PRACTITIONER_COLOR_PALETTE_QUERY_KEY });
				} else {
					showError("Your profile details saved, but your professional info failed to save.");
				}
				return;
			}
		}

		updateSessionUser(userData as User);

		showSuccess("Profile updated successfully");
		setIsEditing(false);
	};


	return (
		<Layout>
			<PageContainer
				title={`${userData?.first_name || ""} ${userData?.last_name || ""}`}
				actions={
					!isEditing
						? [
							{
								label: "Edit Profile",
								icon: <EditOutlined />,
								onClick: () => handleEditToggle(),
								type: "primary",
							},
						]
						: [
							{
								label: "Save",
								icon: <SaveOutlined />,
								onClick: () => handleSave(),
								type: "primary",
							},
						]
				}
			>
				{loading ? (
					<InlineLoading minHeight={400} message="Loading profile data…" />
				) : (
					<>
						<SelfProfileBasicInfoSections user={userData} isEditing={isEditing} setUserData={setUserData} />

						{isStaff && (
							<>
								<SectionCard title="Emergency Contact">
									<UserProfileEmergency teamMember={userData as TeamMember} isEditing={isEditing} setTeamMember={setUserData} />
								</SectionCard>

								<SectionCard title="Schedule">
									<UserProfileSchedule teamMember={userData as TeamMember} isEditing={isEditing} setTeamMember={setUserData} />
								</SectionCard>

								<SectionCard title="Professional">
									<UserProfileProfessional teamMember={userData as TeamMember} isEditing={isEditing} setTeamMember={setUserData} />
								</SectionCard>

								<SectionCard title="Education">
									<UserProfileEducation teamMember={userData as TeamMember} isEditing={isEditing} setTeamMember={setUserData} />
								</SectionCard>
							</>
						)}
						{!isStaff && (
							<SectionCard title="Billing Information">
								<UserProfileBilling />
							</SectionCard>
						)}
					</>
				)}


			</PageContainer>
		</Layout>
	);
};

export default ProfilePage;
